import Cookies from 'js-cookie'
import { AutoCompleteManager } from './AutoComplete'

import { AccessorKeyColumnDef, ColumnDef } from '@tanstack/vue-table'
import { defaultTables, defaultUrls } from './stores/config'
import { GroupsWithColumns, JSSchema, JSSchemaObject, JSSchemaObjectTypes } from './tables'
import { fillColumnsWithDefaults, generateColumns, mergeColumns } from './tables/helpers'
import {
  OperatorObject,
  type ApiResponse,
  type BaseQueryArgs,
  type Environment,
  type ErrorObject,
  type Filters,
  type GetColumns,
  type PaginationArgs,
  type SearchQuery,
  type SignedInUser,
  type ValidationErrorResponse,
} from './types/types'
import { getQueryStringFromFiltersObject } from './utils'

import { HTTP_STATUS_CODES } from './Consts'
import { getBasicUnauthorizedResponse, injectUrlWithParams } from './helpers'
import {
  ApiUrls,
  Config,
  ConfigUrls,
  PejerState,
  PejerTableNames,
  PejerTables,
  PejerTablesList,
  TableConfig,
} from './types/base'
import { ExtractParams } from './types/HelperTypes'
import { PlatformUserRoles } from './types/Roles'
import { SearchableColumn } from './useSearch'

export default class PejerBase<
  L extends TableConfig = typeof defaultTables,
  U extends ConfigUrls = typeof defaultUrls,
  O extends Config<U, L> = Config<U, L>,
> {
  state: PejerState

  // These will be set on init()
  config: O = null
  urls: U = null
  baseURL: string | null = null
  columns:
    | {
        [key in PejerTableNames]: {
          list: ColumnDef<PejerTables[key]['list']>[]
          full: ColumnDef<PejerTables[key]['full']>[]
        }
      }
    | null = null

  constructor(state: PejerState) {
    this.state = state
  }

  setConfig(newConfig: O) {
    this.config = newConfig
    this.urls = newConfig.urls
    return this
  }

  async loadURLConfig() {
    // Local development only: backend chosen by the host page via /dev-backend.html
    if (window.location.hostname === 'localhost') {
      try {
        const devBackendUrl = localStorage.getItem('pejer.devBackendUrl')
        if (devBackendUrl) {
          this.baseURL = devBackendUrl
          return
        }
      } catch (error) {}
    }

    try {
      const localConfigRes = await fetch('/config.local.json')
      if (localConfigRes.ok) {
        const localConfig = await localConfigRes.json()
        this.baseURL = localConfig.backendUrl
        return
      }
    } catch (error) {}

    // Fallback to config.json
    const res = await fetch('/config.json')
    const config = await res.json()
    this.baseURL = (await config).backendUrl
  }

  async init() {
    await this.loadURLConfig()
    const signedInUser = await this.getSignedInUser()
    if ('data' in signedInUser) {
      this.state.user.value = signedInUser.data.user
      this.state.platformInfo.value = signedInUser.data.platformInfo
    }
    this.state.enviroment.value = this.getEnviroment()

    this.state.autoComplete.addColumn(AutoCompleteManager.formatKey('customers', 'name'))
    this.state.autoComplete.addColumn(AutoCompleteManager.formatKey('customers', 'customerId'))

    this.columns = {} as typeof this.columns

    for (const [table, columnDef] of Object.entries(this.config.columns)) {
      this.columns[table] = {}

      if (this.config.schema.tables[table].list)
        this.columns[table].list = mergeColumns(
          fillColumnsWithDefaults<any>(
            this as any,
            this.config.schema.tables[table].list as any,
            columnDef as any
          ) as any,
          generateColumns(this as any, this.config.schema.tables[table].list)
        )

      if (this.config.schema.tables[table].full)
        this.columns[table].full = generateColumns(
          this as any,
          this.config.schema.tables[table].full
        )
      // mergeColumns(
      //     fillColumnsWithDefaults(this, this.config.schema.tables[table].full, columnDef as any),
      //     generateColumns(this, this.config.schema.tables[table].full)
      //   )
    }

    return this
  }

  getColumnsGrouped<T extends PejerTableNames>(table: T): Record<string, ColumnDef<any>[]> {
    return this.columns[table].list.reduce((acc, column) => {
      if (column.meta.group) {
        if (!acc[column.meta.group]) {
          acc[column.meta.group] = []
        }
        acc[column.meta.group].push(column)
      }
      return acc
    }, {})
  }

  getGroups<T extends PejerTableNames, TName extends keyof PejerTables[T] = 'list'>(
    table: T,
    columns?: AccessorKeyColumnDef<PejerTables[T][TName]>[],
    listOrFull: TName = 'list' as TName
  ): GroupsWithColumns<PejerTables[T][TName]> {
    const group = this.config.schema.tables[table]?.groups

    if (!group) return {}

    const cols =
      columns ||
      (this.columns[table][listOrFull as TName] as AccessorKeyColumnDef<PejerTables[T][TName]>[])

    // const flattenedSchema = this.flattenSchemaFields(schema)

    const result = Object.keys(group).reduce(
      (acc, key) => {
        acc[key] = { ...group[key], name: key, columns: cols.filter((x) => x.meta?.group === key) }
        return acc
      },
      {} as GroupsWithColumns<PejerTables[T][TName]>
    )

    return result
  }

  getDefaultColumns<T extends PejerTableNames>(
    table: T
  ): {
    sortingColumn: GetColumns<PejerTables[T]['list']>
    searchableColumns: SearchableColumn<PejerTablesList<T>>[]
  } {
    const entries = Object.entries<JSSchemaObject<any>>(
      this.flattenSchemaFields(this.config.schema.tables[table].list as any)
    )

    const sortingColumn = entries.find(([, value]) => value.isDefaultSortColumn)[0] as GetColumns<
      PejerTables[T]['list']
    >
    const prioritySymbol = Symbol('priority')

    const searchableColumns = entries
      .filter(([, value]) => value.inSearchSelect)
      .map(([key, value]) => ({
        key: key,
        label: value.humanReadable,
        [prioritySymbol]: typeof value.inSearchSelect === 'number' ? value.inSearchSelect : 0,
      }))

    searchableColumns.sort((a, b) => (b[prioritySymbol] as number) - (a[prioritySymbol] as number))
    delete searchableColumns[prioritySymbol]

    if (!sortingColumn)
      throw new Error(
        `No default sorting column found for table: ${table}. Add 'isDefaultSortColumn' to one of the columns in the '${this.config.schema.tables[table].list.name}' schema`
      )

    return {
      sortingColumn,
      searchableColumns: searchableColumns as unknown as SearchableColumn<PejerTablesList<T>>[],
    }
  }

  getListSchema<T extends PejerTableNames>(table: T): JSSchema<PejerTables[T]['list']> {
    return this.config.schema.tables[table]?.list as JSSchema<PejerTables[T]['list']>
  }
  getFullSchema<T extends PejerTableNames>(table: T): JSSchema<PejerTables[T]['full']> {
    return this.config.schema.tables[table]?.full as JSSchema<PejerTables[T]['full']>
  }

  // queryToString<T extends PejerTableNames>(
  //   table: T,
  //   query: Partial<BaseQueryArgs<PejerTables[T]['list']>>
  // ): BaseQueryArgs<PejerTables[T]['list']> {}

  // This function is used to map the query to the correct format
  // e.g divide money by 100 to get the correct value
  mapQueryToFormattedValues<T extends PejerTableNames>(
    table: T,
    query: Partial<BaseQueryArgs<PejerTables[T]['list']>>
  ): BaseQueryArgs<PejerTables[T]['list']> {
    const schema = this.getListSchema(table)
    const mapQuery = (
      query: SearchQuery<PejerTables[T]['list']>,
      shouldFormat: boolean
    ): SearchQuery<PejerTables[T]['list']> => {
      const copy = JSON.parse(JSON.stringify(query)) as BaseQueryArgs<PejerTables[T]['list']>

      for (const [key, value] of Object.entries<SearchQuery<PejerTables[T]['list']>>(
        copy || ({} as BaseQueryArgs<PejerTables[T]['list']>)
      )) {
        const schemaObj = this.getSchemaObjectFromFlattenedKeys(schema as JSSchema, key)
        if (!schemaObj) continue
        const schemaObject = this.getSchemaReferencedObject(schemaObj)
        if (!schemaObject) continue
        const isStringType =
          schemaObject.type === 'string' ||
          schemaObject.type === 'date' ||
          schemaObject.type === 'enum'

        if (shouldFormat && 'formatter' in schemaObject) {
          Object.entries<OperatorObject['=']>(value as any).forEach(([operator, value]) => {
            const rawValue =
              'smartSearchValue' in value && value.smartSearchEnabled && value.smartSearchValue
                ? value.smartSearchValue
                : value.value

            if (value.value && 'to' in schemaObject.formatter) {
              if (typeof rawValue === 'string') {
                copy[key][operator].value = rawValue
                  .split('|')
                  .map(
                    (x) =>
                      (x == 'null'
                        ? schemaObject.formatter.to(null)
                        : schemaObject.formatter.to(x)) ?? 'null'
                  )
                  .join('|')
              } else copy[key][operator].value = schemaObject.formatter.to(rawValue)
            }
          })
        }
        if (isStringType) {
          if (typeof value == 'string') {
            copy[key] = {
              '=': {
                value: `'${value}'`,
              },
            }
          } else if (typeof value == 'object' && value)
            Object.entries<OperatorObject['=']>(value as any).forEach(([operator, value]) => {
              if (typeof value == 'string') {
                copy[key] = {
                  [operator]: {
                    value: `'${value}'`,
                  },
                }
              } else if (typeof value == 'object' && value) {
                const smartSearchEnabled =
                  'smartSearchValue' in value && value.smartSearchEnabled && value.smartSearchValue
                const rawValue = (
                  smartSearchEnabled ? value.smartSearchValue : value.value
                ) as string

                if (rawValue) {
                  copy[key][operator].value = rawValue
                    .split('|')
                    .map((x) => {
                      if (x == 'null') return x
                      if (smartSearchEnabled) return `"%${x}%"`
                      return `"${x}"`
                    })
                    .join('|')
                }
              }
            })
        }
      }

      return copy
    }

    const mappedUserFilters = query.filters?.userFilters
      ? mapQuery(query.filters.userFilters, true)
      : {}
    const mappedStaticFilters = query.filters?.staticFilters
      ? mapQuery(query.filters.staticFilters, false)
      : {}

    return {
      ...query,
      filters: { userFilters: mappedUserFilters, staticFilters: mappedStaticFilters },
    } as BaseQueryArgs<PejerTables[T]['list']>
  }

  // Modifies the data to match the schema
  formatRequestFromSchema<L>(schemaFields: JSSchema['fields'], data: L): L {
    for (const [col, value] of Object.entries(data)) {
      if (!schemaFields[col] || !value) continue
      const schemaObject = this.getSchemaReferencedObject(schemaFields[col])

      if (!schemaObject) continue

      if ('formatter' in schemaObject && 'from' in schemaObject.formatter) {
        data[col] = schemaObject.formatter.from(value, data)
      } else if (schemaObject.type === 'object') {
        this.formatRequestFromSchema(schemaObject.values, value)
      } else if (schemaObject.type === 'array') {
        value.forEach((x) => this.formatRequestFromSchema(schemaObject.values, x))
      } else if (schemaObject.type === 'rawArray') {
        if (!schemaObject.formatter) continue
        value.forEach((x) => (data[col] = schemaObject.formatter.from(x, data)))
      }
    }

    return data
  }

  getSchemaObjectFromFlattenedKeys(JSSchema: JSSchema, key: string): JSSchemaObject {
    // we want to return the schema object with the key, nested objects have paths with dots
    const keys = key.split('.')
    let schemaObject = JSSchema.fields[keys[0]] as JSSchemaObjectTypes
    if (!schemaObject) return null

    for (let i = 1; i < keys.length; i++) {
      if (schemaObject.type === 'object') {
        schemaObject = schemaObject.values[keys[i]] as JSSchemaObjectTypes
      } else if (schemaObject.type === 'array') {
        schemaObject = schemaObject.values[keys[i]] as JSSchemaObjectTypes
      }
    }

    return schemaObject
  }

  flattenSchemaFields(schema: JSSchema): JSSchema['fields'] {
    const flattenedArray = this._flattenSchema(schema.fields, '', undefined)

    const result: JSSchema['fields'] = {}
    for (const obj of flattenedArray) {
      result[obj._key] = obj
      delete obj._key
    }
    return result
  }

  _flattenSchema(
    schema: JSSchema['fields'],
    prefix: string,
    previousMetaGroup?: string
  ): (JSSchemaObject & { _key: string })[] {
    const result: (JSSchemaObject & { _key: string })[] = []

    if (!schema) return result

    for (const [col, value] of Object.entries(schema)) {
      if (!value) continue

      const schemaObject = this.getSchemaReferencedObject(value)

      if (!schemaObject) continue

      const key = prefix ? prefix + '.' + col : col
      result.push({
        ...schemaObject,
        _key: key,
        meta: { ...schemaObject.meta, group: schemaObject.meta?.group ?? previousMetaGroup },
      })

      if (schemaObject.type === 'object' || schemaObject.type === 'array')
        result.push(
          ...this._flattenSchema(
            schemaObject.values,
            key,
            schemaObject.meta?.group ?? previousMetaGroup
          )
        )
    }

    return result
  }

  getSchemaReferencedObject(schemaObject: JSSchemaObject): JSSchemaObjectTypes {
    if (!schemaObject) return null

    const relation =
      ((schemaObject as any).relation as string) ?? ((schemaObject as any).foreignKey as string)
    if (relation) {
      const [table, field] = relation.split('.')

      if (!field && !('foreignKey' in schemaObject))
        throw new Error('No foreign key in relation: ' + relation)

      // const fieldSchema = 'foreignKey' in schemaObject ? schemaObject.foreignKey : field

      const relatedObject = this.getListSchema(table as PejerTableNames).fields[field]

      const result = {
        ...relatedObject,
        // table: table
      }

      if ('humanReadable' in schemaObject) {
        result.humanReadable = schemaObject.humanReadable
      }

      return { ...result, ...schemaObject } as JSSchemaObjectTypes
    }

    return { ...schemaObject } as JSSchemaObjectTypes
  }

  async signOutUser() {
    this.state.user.value = null

    // Delete the autocomplete columns and create a new one to remove sensitive data from the previous user
    this.state.autoComplete = new AutoCompleteManager()

    Cookies.remove(this.config.cookieNames.platformToken)
  }

  async signInUser(email: string, password: string): Promise<ApiResponse<SignedInUser | null>> {
    const res = await this.fetch(this.urls.routes.signIn, {
      body: { email, password },
      ignoreAuthorization: true,
    })

    if ('error' in res) {
      console.error('Could not sign in', res)
      return res
    }

    this.setPlatformCookie(res.data as string)
    return await this.getSignedInUser()
  }

  isPermitted(url: ApiUrls) {
    return (
      !url.permissions ||
      this.state.user.value.roles.findIndex(
        (x) =>
          x === PlatformUserRoles.Admin ||
          x === PlatformUserRoles.Owner ||
          url.permissions.includes(x)
      ) !== -1
    )
  }

  hasAnyPermissions(urls: ConfigUrls<any>[any]) {
    for (const url in urls) {
      if (this.isPermitted(urls[url])) return true
    }
    return false
  }

  /**
   * This function will return the missing permissions for the user for a whole route,
   * if the user is missing any of the permissions for a route, the whole route will be blocked
   * and the missing permissions will be returned
   */
  getMissingPermissions(urls: ConfigUrls<any>[any]) {
    const permissions = new Set<string>()
    for (const url in urls) {
      if (this.isPermitted(urls[url])) return []
      urls[url].permissions.forEach((x) => permissions.add(x))
    }
    return Array.from(permissions)
  }

  async getSignedInUser(): Promise<ApiResponse<SignedInUser>> {
    const token = Cookies.get(this.config.cookieNames.platformToken)

    if (!token) {
      return {
        statusCode: HTTP_STATUS_CODES.UNAUTHORIZED,
        isUnauthorized: true,
        httpError: 'No token in header',
        error: 'No token in header',
      }
    }

    const res = (await this.fetch(this.urls.routes.validateJWT, {
      ignoreAuthorization: true,
    })) as ApiResponse<SignedInUser>

    // the token is most likely invalid so we remove it
    if ('error' in res) Cookies.remove(this.config.cookieNames.platformToken)

    if ('data' in res) {
      this.state.user.value = res.data.user
      this.state.platformInfo.value = res.data.platformInfo
    }

    return res as ApiResponse<SignedInUser>
  }

  getEnviroment(): Environment {
    return (Cookies.get(this.config.cookieNames.enviroment) as Environment) || 'test'
  }

  static hasCookie(name: string) {
    return !!Cookies.get(name)
  }

  setPlatformCookie(token: string) {
    // x-token-platform: 'platform-token'
    // x-enviroment: "test" | "production"
    // toggle between test and prod

    const currentTime = new Date().getTime()
    const updatedTime = new Date(currentTime + 6 * 60 * 60 * 1000)
    Cookies.set(this.config.cookieNames.platformToken, token, {
      expires: updatedTime, // expires in 6 hours
      // domain: 'platformapi.bloc.net'
      sameSite: 'strict',
    })
  }

  setEnviroment(env: 'test' | 'production') {
    // x-enviroment: "test" | "production"
    // toggle between test and prod

    Cookies.set(this.config.cookieNames.enviroment, env, { expires: 365 })
    this.state.enviroment.value = env
  }

  isLoggedIn() {
    const logginToken = Cookies.get(this.config.cookieNames.platformToken)

    if (!this.state.user.value && logginToken) this.getSignedInUser()

    return !!logginToken
  }

  getHeaders() {
    const logginToken = Cookies.get(this.config.cookieNames.platformToken)

    return {
      [this.config.cookieNames.platformToken]: logginToken || '',
      [this.config.cookieNames.enviroment]: this.getEnviroment(),
    }
  }

  fetchManyAbortControllers: Record<string, AbortController> = {}
  getAbortController(url: ApiUrls) {
    const abortKey = url.path + ':' + url.method

    if (this.fetchManyAbortControllers[abortKey])
      this.fetchManyAbortControllers[abortKey].abort(
        'Aborting previous fetch as a new one is being made'
      )

    this.fetchManyAbortControllers[abortKey] = new AbortController()
    return this.fetchManyAbortControllers[abortKey]
  }

  async fetchPejerData<L, K = BaseQueryArgs<L> | PaginationArgs>(
    url: ApiUrls,
    query?: K,
    abortSignal?: AbortSignal
  ): Promise<ApiResponse<L[]>> {
    if (url.method === 'GET') {
      let urlWithParams = url.path

      if (query) {
        const encodedQuery =
          'filters' in (query as Record<string, unknown>)
            ? {
                ...query,
                filters: getQueryStringFromFiltersObject(
                  (query as { filters: Filters<L> }).filters
                ),
              }
            : query

        urlWithParams +=
          '?' + new URLSearchParams(encodedQuery as unknown as Record<string, string>)
      }

      return await this.handleFetch(
        () =>
          fetch(this.baseURL + urlWithParams, {
            signal: abortSignal,
            method: url.method,
            headers: { ...this.getHeaders() },
          }),
        { abortSignal: abortSignal }
      )
    }

    const encodedQuery =
      'filters' in (query as Record<string, unknown>)
        ? {
            ...query,
            filters: getQueryStringFromFiltersObject<L>(
              (query as { filters: SearchQuery<L> }).filters
            ),
          }
        : query
    return await this.handleFetch(
      () =>
        fetch(this.baseURL + url.path, {
          signal: abortSignal,
          method: url.method,
          headers: { 'Content-Type': 'application/json', ...this.getHeaders() },
          body: JSON.stringify(encodedQuery || {}),
        }),
      { abortSignal: abortSignal }
    )
  }

  async getOne<
    // String literal type of table name eg. 'payments' || 'customers'
    T extends PejerTableNames,
  >(
    table: T,
    id: string | Record<string, string | number>
  ): Promise<ApiResponse<PejerTables[T]['full']>> {
    const res = (await this.fetch(this.urls[table].get, {
      pathArgs: typeof id === 'string' ? { id } : id,
    })) as ApiResponse<PejerTables[T]['full']>

    if ('error' in res) return res

    const schema = this.getFullSchema(table as PejerTableNames)
    if (!schema) return res
    this.formatRequestFromSchema(schema.fields, res.data)

    return res
  }

  async getMany<
    // String literal type of table name eg. 'payments' || 'customers'
    T extends PejerTableNames,
    L extends PejerTables[T]['list'],
  >(
    table: T,
    query: Partial<BaseQueryArgs<PejerTables[T]['list']>>,
    options?: { url?: string; abortSignal?: AbortSignal }
  ): Promise<ApiResponse<L[]>> {
    const url: ApiUrls = {
      path: options?.url || this.urls[table].list.path,
      method: this.urls[table].list.method,
    }
    const abortSignal = options?.abortSignal ?? this.getAbortController(url).signal

    const mappedQuery = this.mapQueryToFormattedValues(table, query)
    const res = await this.fetchPejerData<L>(url, mappedQuery, abortSignal)
    if ('error' in res) return res

    const schema = this.getListSchema(table as PejerTableNames)

    if (!schema) return res

    res.data.forEach((x) => {
      this.formatRequestFromSchema(schema.fields, x)
    })

    if (!this.state.autoComplete) return res

    // Adding the data to the autocomplete manager
    res.data.forEach((x) => {
      for (const col in x) {
        if (!schema[col as string]) continue
        const schemaObject = this.getSchemaReferencedObject(schema[col as string])

        if (!schemaObject) continue

        // TODO: ignoring nested objects/arrays for now
        if (schemaObject.type != 'string') continue

        const hasRelation: boolean =
          'relation' in schema[col as string] && (schema[col as string] as any).relation

        const tableColumn = hasRelation
          ? ((schema[col as string] as any).relation as string)
          : AutoCompleteManager.formatKey(String(table), String(col))

        // Adding the data to the autocomplete manager
        this.state.autoComplete?.addNameIfInitiated(tableColumn, String(x[col as keyof L]))
      }
    })

    return res
  }

  // This will be inherited by the Pejer class
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected onUnauthorized(res: ErrorObject) {
    res.isUnauthorized = true
    return res
  }

  async fetch<T extends ApiUrls<K, any>, L = Parameters<T['formatter']>[0], K = unknown>(
    apiUrl: T,
    options?: {
      pathArgs?: Record<ExtractParams<T['path']>, string | number>
      body?: any
      ignoreAuthorization?: boolean
      abortSignal?: AbortSignal
    }
  ): Promise<ApiResponse<L>> {
    let urlWithParams = apiUrl.path
    if (options?.pathArgs) urlWithParams = injectUrlWithParams(urlWithParams, options.pathArgs)

    if (urlWithParams.includes('{')) {
      const missingParams = urlWithParams.match(/{(.*?)}/g)
      throw new Error('Missing path arguments: ' + missingParams)
    }

    // All GET requests should have the body in the url as this is what dotnet supports
    if (apiUrl.method === 'GET') {
      const urlWithBody = urlWithParams + '?' + new URLSearchParams(options?.body)
      const res = await this.handleFetch<L>(
        () =>
          fetch(this.baseURL + urlWithBody, {
            method: apiUrl.method,
            headers: { ...this.getHeaders() },
            signal: options?.abortSignal,
          }),
        { ignoreAuthorization: options?.ignoreAuthorization ?? apiUrl.unauthenticatedAccess }
      )
      if ('error' in res) return res
      return {
        ...res,
        data: apiUrl.formatter ? (apiUrl.formatter(res.data) as L) : (res.data as L),
      }
    }

    const body = options?.body
      ? typeof options.body === 'object'
        ? JSON.stringify(options?.body)
        : options.body
      : undefined

    const res = await this.handleFetch<L>(
      () =>
        fetch(this.baseURL + urlWithParams, {
          method: apiUrl.method,
          headers: { ...this.getHeaders(), 'Content-Type': 'application/json' },
          body,
          signal: options?.abortSignal,
        }),
      { ignoreAuthorization: options?.ignoreAuthorization ?? apiUrl.unauthenticatedAccess }
    )
    if ('error' in res) return res
    return { ...res, data: apiUrl.formatter ? (apiUrl.formatter(res.data) as L) : (res.data as L) }
  }

  protected async handleFetch<L>(
    fetchFunction: () => Promise<Response>,
    options?: { ignoreAuthorization?: boolean; abortSignal?: AbortSignal }
  ): Promise<ApiResponse<L>> {
    if (!options?.ignoreAuthorization && !this.isLoggedIn()) {
      const error = getBasicUnauthorizedResponse()
      console.error(error)
      return error
    }

    const getResponse = async () => {
      let res: Response
      try {
        res = await fetchFunction()
      } catch (e) {
        return {
          statusCode: HTTP_STATUS_CODES.INTERNAL_SERVER_ERROR,
          httpError: 'Internal server error',
          error: 'Internal server error',
          rawError: e,
          wasAborted: options?.abortSignal?.aborted ?? false,
        }
      }

      const contentType: string = res.headers.get('content-type') ?? ''
      if (!res.ok) {
        if (contentType.includes('application/problem+json')) {
          try {
            const json = (await res.json()) as ValidationErrorResponse

            return {
              statusCode: res.status,
              httpError: res.statusText,
              error: json.title || JSON.stringify(json, null, 2),
              problemDetails: json,
              validationErrors: json.errors,
            }
          } catch (e) {
            return {
              statusCode: res.status,
              httpError: res.statusText,
              error: 'Unexpected error',
              rawError: e,
              wasAborted: options?.abortSignal?.aborted ?? false,
            }
          }
        }

        if (contentType.includes('application/json')) {
          try {
            const json = await res.json()

            return {
              statusCode: res.status,
              httpError: res.statusText,
              error: JSON.stringify(json, null, 2),
            }
          } catch (e) {
            return {
              statusCode: res.status,
              httpError: res.statusText,
              error: 'Unexpected error',
              rawError: e,
              wasAborted: options?.abortSignal?.aborted ?? false,
            }
          }
        }

        let text: string
        let errorObject: Error | null = null
        try {
          text = await res.text()
        } catch (e) {
          text = 'Unexpected error'
          errorObject = e as Error
        }
        if (res.status === HTTP_STATUS_CODES.NOT_FOUND) {
          return {
            statusCode: res.status,
            httpError: res.statusText || 'Not found',
            error: text || 'Not found',
            rawError: errorObject,
            wasAborted: options?.abortSignal?.aborted ?? false,
          }
        }

        return {
          statusCode: res.status,
          httpError: res.statusText,
          error: text,
          rawError: errorObject,
          wasAborted: options?.abortSignal?.aborted ?? false,
        }
      }

      if (contentType.includes('application/pdf')) {
        try {
          const blob = await res.blob()
          const url = URL.createObjectURL(blob)
          return { statusCode: res.status, data: url as L }
        } catch (e) {
          return {
            statusCode: res.status,
            httpError: res.statusText,
            error: 'Could not parse PDF',
            rawError: e,
            wasAborted: options?.abortSignal?.aborted ?? false,
          }
        }
      }

      if (contentType.includes('application/json')) {
        try {
          const json = (await res.json()) as L
          return { statusCode: res.status, data: json }
        } catch (e) {
          return { statusCode: res.status, data: {} as L }
        }
      }

      return { statusCode: res.status, data: (await res.text()) as L }
    }

    const res = await getResponse()
    if (res.statusCode === HTTP_STATUS_CODES.UNAUTHORIZED) {
      return this.onUnauthorized(res as ErrorObject)
    }
    return res as any
  }
}
